import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import AuthMedia from "../components/AuthMedia";
import DeathProcess from "../components/DeathProcess";
import { ScrollText, Lock, BookHeart, PenLine, Scale } from "lucide-react";

const TestamentoVista = () => {
  const { token } = useParams();
  const [data, setData] = useState(undefined);

  const load = () => api.get(`/testament/public/${token}`).then((res) => setData(res.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [token]);

  if (data === undefined)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento…</div>;

  if (data === null)
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center bg-white rounded-3xl border border-border p-12 max-w-md" data-testid="testament-invalid">
          <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-2">Link non valido</h1>
          <p className="text-muted-foreground">Questo link non è valido o il testamento non esiste.</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen">
      <header className="border-b border-border bg-[#F9F6F0]/85 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-2">
          <BookHeart className="w-6 h-6 text-primary" />
          <span className="font-serif text-xl">Eterna</span>
          <span className="ml-auto text-sm text-muted-foreground">Testamento</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 fade-in">
        {data.process && data.process.death_status !== "alive" && (
          <DeathProcess token={token} process={data.process} ownerName={data.owner.name} onUpdated={load} />
        )}
        {!data.available ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-border" data-testid="testament-not-available">
            <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
            <h1 className="font-serif text-2xl mb-2">Non ancora disponibile</h1>
            <p className="text-muted-foreground max-w-sm mx-auto">Il testamento di {data.owner.name} sarà consultabile secondo le disposizioni stabilite.</p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-border p-8" data-testid="testament-view">
            <div className="flex items-center gap-2 mb-4">
              <ScrollText className="w-6 h-6 text-primary" />
              <h1 className="font-serif text-3xl">Testamento di {data.owner.name}</h1>
            </div>
            {data.testament.deposit_type === "notary" ? (
              <div data-testid="testament-notary-deposit">
                <div className="flex items-start gap-3 bg-[#EAF3EE] border border-[#81B29A]/40 rounded-xl p-4 mb-5">
                  <Scale className="w-5 h-5 text-[#4e8f6f] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-[#2C2A29]">Testamento depositato presso un notaio</p>
                    <p className="text-sm text-muted-foreground">L'originale è custodito dal notaio. Di seguito i riferimenti per recuperarlo.</p>
                  </div>
                </div>
                <dl className="space-y-3 text-sm">
                  {data.notary?.name && (<div className="flex gap-2"><dt className="text-muted-foreground w-40 shrink-0">Notaio</dt><dd className="text-[#2C2A29] font-medium">{data.notary.name}</dd></div>)}
                  {data.notary?.email && (<div className="flex gap-2"><dt className="text-muted-foreground w-40 shrink-0">Contatto</dt><dd className="text-[#2C2A29]">{data.notary.email}</dd></div>)}
                  {data.testament.notary_office && (<div className="flex gap-2"><dt className="text-muted-foreground w-40 shrink-0">Studio / città</dt><dd className="text-[#2C2A29]">{data.testament.notary_office}</dd></div>)}
                  {data.testament.notary_repertory && (<div className="flex gap-2"><dt className="text-muted-foreground w-40 shrink-0">N. repertorio/raccolta</dt><dd className="text-[#2C2A29]">{data.testament.notary_repertory}</dd></div>)}
                  {data.testament.deposited_at && (<div className="flex gap-2"><dt className="text-muted-foreground w-40 shrink-0">Data del deposito</dt><dd className="text-[#2C2A29]">{new Date(data.testament.deposited_at).toLocaleDateString("it-IT", { dateStyle: "long" })}</dd></div>)}
                </dl>
                {data.testament.content && <p className="whitespace-pre-wrap leading-relaxed text-[#3d3a38] mt-5 pt-5 border-t border-border">{data.testament.content}</p>}
              </div>
            ) : (
            <>
            {data.testament.content && <p className="whitespace-pre-wrap leading-relaxed text-[#3d3a38] mb-6">{data.testament.content}</p>}
            {data.testament.files?.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Documenti allegati</p>
                {data.testament.files.map((f) => (
                  <div key={f.storage_path} className="rounded-xl overflow-hidden border border-border">
                    {f.kind === "image"
                      ? <AuthMedia item={f} guardianToken={token} className="w-full max-h-[600px] object-contain bg-[#F4F1EA]" />
                      : <div className="p-4 bg-[#F4F1EA]"><AuthMedia item={f} guardianToken={token} /></div>}
                  </div>
                ))}
              </div>
            )}
            {data.testament.signed && data.testament.signed_at && (
              <div className="mt-6 pt-6 border-t border-border flex items-center gap-3" data-testid="testament-view-signature">
                <PenLine className="w-5 h-5 text-[#81B29A] shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-[#2C2A29]">Firmato digitalmente da {data.testament.signature}</p>
                  <p className="text-muted-foreground">il {new Date(data.testament.signed_at).toLocaleDateString("it-IT", { dateStyle: "long" })}</p>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TestamentoVista;
